import React from 'react';
import { useTranslation } from 'react-i18next';
import { type ButtonProps, HStack, Text } from '@chakra-ui/react';
import { RiGlobalLine } from 'react-icons/ri';
import { Button } from '@/components/ui/button';
import { MenuContent, MenuRadioItem, MenuRadioItemGroup, MenuRoot, MenuTrigger } from '@/components/ui/menu';
import {
  isI18nEnabled,
  languageFlags,
  languageNames,
  setLanguage,
  type SupportedLanguage,
  supportedLanguages
} from '@/libs/i18n';

interface LanguageSelectorProps {
  /** Styling overrides for the trigger, e.g. white text on the mobile brand nav. */
  buttonProps?: ButtonProps;
}

export const LanguageSelector = ({ buttonProps }: LanguageSelectorProps) => {
  const { i18n } = useTranslation();

  // Resetting to English when i18n is off is handled centrally in _app.tsx.
  if (!isI18nEnabled()) return null;

  const currentLang = (i18n.language?.substring(0, 2) || 'en') as SupportedLanguage;

  const handleLanguageChange = (details: { value: string }) => {
    setLanguage(details.value as SupportedLanguage);
  };

  return (
    <MenuRoot positioning={{ placement: 'bottom-end' }}>
      <MenuTrigger asChild>
        <Button variant='ghost' size='md' aria-label='Select language' {...buttonProps}>
          <HStack gap={1}>
            <RiGlobalLine fontSize='1.15rem' />
            <Text fontSize='sm' fontWeight='medium'>
              {currentLang.toUpperCase()}
            </Text>
          </HStack>
        </Button>
      </MenuTrigger>
      <MenuContent minW='160px'>
        <MenuRadioItemGroup value={currentLang} onValueChange={handleLanguageChange}>
          {supportedLanguages.map((lang) => (
            <MenuRadioItem key={lang} value={lang} cursor='pointer'>
              <HStack gap={2}>
                <Text fontSize='md'>{languageFlags[lang]}</Text>
                <Text>{languageNames[lang]}</Text>
              </HStack>
            </MenuRadioItem>
          ))}
        </MenuRadioItemGroup>
      </MenuContent>
    </MenuRoot>
  );
};
